'use client';
import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import NextLink from 'next/link';
import {trackHeaderViewAllClick, trackHeaderViewItemClick} from '../../../analytics';
import type {TrackedItem} from '../../../analytics/shared';
import {Button, MenuLink, Typography} from '../../primitives';
import {VIEW_ALL_THRESHOLD} from './constants';

export interface MegaMenuItem {
  key: string;
  name: string;
  href: string;
}

interface MegaMenuSectionProps {
  label: string;
  items: MegaMenuItem[];
  viewAllHref: string;
  viewAllLabel: string;
  viewAllItem: TrackedItem;
  onNavigate: () => void;
}

const Section = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.25),
}));

const ItemLink = styled(MenuLink)(({theme}) => ({
  '&:hover': {color: theme.palette.colors.text.neutral.inverse},
}));

export function MegaMenuSection({label, items, viewAllHref, viewAllLabel, viewAllItem, onNavigate}: MegaMenuSectionProps) {
  return (
    <Section>
      <Typography
        as="span"
        family="primary"
        size="body-3"
        weight="medium"
        textTransform="uppercase"
        sx={theme => ({letterSpacing: '-0.015em', color: theme.palette.colors.text.neutral.inverseMuted, mb: 0.5})}
      >
        {label}
      </Typography>
      {items.slice(0, VIEW_ALL_THRESHOLD).map(item => (
        <ItemLink
          key={item.key}
          weight="regular"
          href={item.href}
          onClick={() => {
            trackHeaderViewItemClick({id: item.key, name: item.name});
            onNavigate();
          }}
        >
          {item.name}
        </ItemLink>
      ))}
      {items.length > VIEW_ALL_THRESHOLD && (
        <Button
          variant="link"
          component={NextLink}
          href={viewAllHref}
          onClick={() => {
            trackHeaderViewAllClick(viewAllItem);
            onNavigate();
          }}
          labelProps={{size: 'body-2', weight: 'regular'}}
          sx={theme => ({
            color: theme.palette.colors.text.primary.default,
            width: 'fit-content',
          })}
        >
          {viewAllLabel}
        </Button>
      )}
    </Section>
  );
}

MegaMenuSection.displayName = 'MegaMenuSection';
